import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { USER_GROUP_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-registry-select.dto';
import { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { BaseMultiSelectComponent } from '@zaoblako/web/shared/components/base-multi-select/base-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'crm-user-group-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent],
  templateUrl: './user-group-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserGroupMultiSelectComponent extends BaseMultiSelectComponent<UserGroupFindInterface> implements OnInit, OnChanges {
  public availableModule = 'BASIS';
  protected readonly entityCacheKind = 'user-group';

  public get multiSelect() {
    return this.multiSelectComponent?.multiSelect;
  }

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public customRequest = false;
  @Input() public scrollHeight: string = '300px';
  @Input() public sort: string;
  @Input() public selectLabel = 'Группы';
  @Input() public display = 'chip';
  @Input() public moveDown = false;

  @Input() public userGroups: UserGroupFindInterface[] = [];
  @Output() public selectedUserGroupsChange = new EventEmitter<UserGroupFindInterface[]>();

  @Input() public set selectedUserGroups(value: UserGroupFindInterface[]) {
    this.selectedEntities = value;
  }

  @Input() public set setSelectedUserGroupIds(ids: number[]) {
    this.setSelectedEntityIds = ids;
  }

  public constructor(private readonly userGroupService: UserGroupService) {
    super();
  }

  public override ngOnInit(): void {
    this.customSearch = this.customRequest && !!this.userGroups?.length;
    if (this.customSearch && this.userGroups?.length) {
      this.entities = this.userGroups;
    }
    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.customRequest || changes.userGroups) {
      this.customSearch = this.customRequest && !!this.userGroups?.length;
      if (this.customSearch && this.userGroups?.length) {
        this.entities = this.userGroups ?? [];
        this.cdRef.markForCheck();
      } else if (!this.customSearch && !changes.customRequest?.firstChange) {
        this.firstLoad();
      }
    }
    if (changes.userGroups && this.customSearch) {
      this.entities = this.userGroups ?? [];
      this.cdRef.markForCheck();
    }
    super.ngOnChanges(changes);
  }

  public findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<UserGroupFindInterface>> {
    return this.userGroupService.findRegistryPage({
      ...queryParams,
      limit: queryParams?.limit ?? DEFAULT_LIMIT,
      offset: queryParams?.offset ?? DEFAULT_OFFSET,
      select: [...USER_GROUP_WIDGET_REGISTRY_SELECT]
    }).pipe(
      map(({ items, count, limit, offset }) => ({ items, count, limit, offset }))
    );
  }

  public onSelect(userGroups: UserGroupFindInterface[]): void {
    if (this.shouldIgnoreSelectionChange(userGroups)) {
      return;
    }

    this.selectedUserGroupsChange.emit(userGroups);
    super.onSelect(userGroups);
  }
}
